/**
 * WeaveIn.ai Platform - Add Contact Form
 * Minimal-field creation form with real-time duplicate detection
 */

import React, { useState, useCallback, useEffect } from 'react';
import { X, UserPlus, AlertTriangle, CheckCircle, Loader2, Edit } from 'lucide-react';
import useContacts from '@/hooks/useContacts';
import { contactsAPI, ContactCreate, DuplicateCheckResult } from '@/services/api/contacts';
import DuplicateMatchPanel from './DuplicateMatchPanel';
import PhoneInput from '@/components/ui/PhoneInput';

interface AddContactFormProps {
  onClose: () => void;
  onCreated?: (contactId: number, duplicates?: DuplicateCheckResult[]) => void;
  contactId?: number;  // If provided, edit mode
  defaultProgramId?: number;
  programs?: Array<{ id: number; name: string }>;
}

const INTAKE_SOURCES = [
  { value: 'interest_form', label: 'Interest Form' },
  { value: 'qr_survey', label: 'QR Code Survey' },
  { value: 'referral_list', label: 'Referral List' },
  { value: 'ai_sourced', label: 'AI Sourced' },
  { value: 'receptionist', label: 'Receptionist / Walk-in' },
  { value: 'mif', label: 'MIF (Member Information Form)' },
  { value: 'file_upload', label: 'File Upload' },
  { value: 'digital_form', label: 'Digital Form' },
  { value: 'marketing', label: 'Marketing Campaign' },
  { value: 'manual', label: 'Manual Entry' },
];

export default function AddContactForm({ onClose, onCreated, contactId, defaultProgramId, programs }: AddContactFormProps) {
  const { createContact, updateContact, checkDuplicates } = useContacts({ autoFetch: false });
  const isEditMode = !!contactId;

  const [form, setForm] = useState<ContactCreate>({
    first_name: '',
    last_name: '',
    phone_no: '',
    email: '',
    dob: '',
    intake_source: 'manual',
    program_id: defaultProgramId,
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveMatches, setLiveMatches] = useState<DuplicateCheckResult[]>([]);
  const [checkingDupes, setCheckingDupes] = useState(false);
  const [loadingContact, setLoadingContact] = useState(false);

  // Load existing contact data in edit mode
  useEffect(() => {
    if (!contactId) return;
    setLoadingContact(true);
    contactsAPI.getContact(contactId).then((c) => {
      setForm({
        first_name: c.first_name || '',
        last_name: c.last_name || '',
        phone_no: c.phone_no || '',
        email: c.email || '',
        dob: c.date_of_birth || '',
        gender: c.gender || undefined,
        race: c.race || undefined,
        ethnicity: c.ethnicity || undefined,
        language: c.language || undefined,
        // TRUS-738: no 'English' default; blank until someone enters it.
        preferred_language: c.preferred_language || undefined,
        street_address: c.street_address || undefined,
        city: c.city || undefined,
        state: c.state || undefined,
        zip_code: c.zip_code || undefined,
        country: c.country || 'USA',
        intake_source: c.intake_source || 'manual',
      });
    }).catch(() => {}).finally(() => setLoadingContact(false));
  }, [contactId]);

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Debounced duplicate check as user types
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerDupCheck = useCallback(
    (updatedForm: ContactCreate) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(async () => {
        const hasEnoughData = updatedForm.first_name && (updatedForm.phone_no || updatedForm.email || updatedForm.last_name);
        if (!hasEnoughData) {
          setLiveMatches([]);
          return;
        }

        setCheckingDupes(true);
        try {
          const matches = await checkDuplicates({
            first_name: updatedForm.first_name,
            last_name: updatedForm.last_name,
            phone: updatedForm.phone_no,
            email: updatedForm.email,
            dob: updatedForm.dob,
          });
          setLiveMatches(matches);
        } catch {
          // silent
        } finally {
          setCheckingDupes(false);
        }
      }, 600);
    },
    [checkDuplicates]
  );

  const handleFieldChange = (field: string, value: string) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    // Trigger dedup on key fields
    if (['first_name', 'last_name', 'phone_no', 'email', 'dob'].includes(field)) {
      triggerDupCheck(updated);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && contactId) {
        const ok = await updateContact(contactId, form);
        if (ok) {
          setSuccess(true);
          setTimeout(() => { onCreated?.(contactId); onClose(); }, 800);
        } else {
          setError('Failed to update contact');
        }
      } else {
        const { contact, duplicates } = await createContact(form);
        if (contact) {
          setSuccess(true);
          setTimeout(() => { onCreated?.(contact.id, duplicates); onClose(); }, 800);
        } else {
          setError('Failed to create contact');
        }
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} contact`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 pt-4 sm:pt-12">
      <div className="mx-3 mb-8 w-full max-w-3xl rounded-xl bg-white shadow-xl dark:bg-gray-900 sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-2">
            {isEditMode ? <Edit className="h-5 w-5 flex-shrink-0 text-blue-600" /> : <UserPlus className="h-5 w-5 flex-shrink-0 text-blue-600" />}
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">{isEditMode ? 'Edit Contact' : 'New Contact'}</h2>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.first_name}
                onChange={(e) => handleFieldChange('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="First name"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
              <input
                type="text"
                value={form.last_name || ''}
                onChange={(e) => handleFieldChange('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="Last name"
              />
            </div>

            {/* Contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <PhoneInput
                value={form.phone_no || ''}
                onChange={(v) => handleFieldChange('phone_no', v)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="(xxx) xxx-xxxx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={form.email || ''}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                placeholder="email@example.com"
              />
            </div>

            {/* DOB */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth</label>
              <input
                type="date"
                value={form.dob || ''}
                onChange={(e) => handleFieldChange('dob', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
              />
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Intake Source</label>
              <select
                value={form.intake_source || ''}
                onChange={(e) => updateField('intake_source', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Select source...</option>
                {INTAKE_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            {/* Program */}
            {!isEditMode && programs && programs.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Program</label>
                <select
                  value={form.program_id ?? ''}
                  onChange={(e) => updateField('program_id', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="">No program</option>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            {/* Demographics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender</label>
              <select value={form.gender || ''} onChange={(e) => updateField('gender', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                <option value="">Select...</option><option value="Male">Male</option><option value="Female">Female</option><option value="Non-Binary">Non-Binary</option><option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Race</label>
              <select value={form.race || ''} onChange={(e) => updateField('race', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                <option value="">Select...</option><option value="American Indian or Alaska Native">American Indian or Alaska Native</option><option value="Asian">Asian</option><option value="Black or African American">Black or African American</option><option value="Native Hawaiian or Other Pacific Islander">Native Hawaiian or Other Pacific Islander</option><option value="White">White</option><option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ethnicity</label>
              <select value={form.ethnicity || ''} onChange={(e) => updateField('ethnicity', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100">
                <option value="">Select...</option><option value="Hispanic or Latino">Hispanic or Latino</option><option value="Not Hispanic or Latino">Not Hispanic or Latino</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
              <input type="text" value={form.preferred_language || ''} onChange={(e) => updateField('preferred_language', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="English" />
            </div>

            {/* Address */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Street Address</label>
              <input type="text" value={form.street_address || ''} onChange={(e) => updateField('street_address', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="123 Main St" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
              <input type="text" value={form.city || ''} onChange={(e) => updateField('city', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="City" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
              <input type="text" value={form.state || ''} onChange={(e) => updateField('state', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="CA" maxLength={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zip Code</label>
              <input type="text" value={form.zip_code || ''} onChange={(e) => updateField('zip_code', e.target.value || undefined)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="94105" maxLength={10} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
              <input type="text" value={form.country || 'USA'} onChange={(e) => updateField('country', e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-gray-100" />
            </div>
          </div>

          {/* Real-time Duplicate Detection Panel */}
          {(liveMatches.length > 0 || checkingDupes) && (
            <div className="mt-5 p-4 border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                {checkingDupes ? (
                  <>
                    <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />
                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Checking for duplicates...</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700 dark:text-orange-300">
                      {liveMatches.length} potential match{liveMatches.length !== 1 ? 'es' : ''} found
                    </span>
                  </>
                )}
              </div>
              {liveMatches.map((match, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-white dark:bg-gray-800 rounded mb-1.5 text-sm">
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {match.first_name} {match.last_name}
                    </span>
                    <span className="ml-2 text-gray-400 text-xs">{match.member_number}</span>
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${match.record_type === 'member' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {match.record_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {match.phone_masked && <span className="text-xs text-gray-400">{match.phone_masked}</span>}
                    <span className={`text-xs font-bold ${match.match_score >= 60 ? 'text-red-600' : 'text-orange-500'}`}>
                      {match.match_score}%
                    </span>
                  </div>
                </div>
              ))}
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                You can still create this contact. Duplicates will be flagged for review.
              </p>
            </div>
          )}

          {/* Error / Success */}
          {error && <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg">{error}</div>}
          {success && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm rounded-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Contact created successfully
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || success}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditMode ? <Edit className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
              {isEditMode ? 'Save Changes' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
